import React from "react";
import CaseStudy from "../case/CaseStudy";
import {
  Section,
  Figure,
  Stage,
  Specs,
  Annotated,
  Grid,
} from "../case/blocks";
import { useStageParallax } from "../case/motion";
import "../case/case.scss";
import "./vsc.scss";

import imgBgIntro from "../../../images/vsc/fig-bg-intro.png";
import imgRenderOverview from "../../../images/vsc/fig-render-overview.png";
import imgRenderBag from "../../../images/vsc/fig-render-bag.png";
import imgRenderSealer from "../../../images/vsc/fig-render-sealer.png";
import imgSteamCooking from "../../../images/vsc/fig-steam-cooking.jpg";
import imgVeg from "../../../images/vsc/fig-veg.png";
import imgSalmon from "../../../images/vsc/fig-salmon.png";
import imgFood from "../../../images/vsc/fig-food.png";
import imgSb1 from "../../../images/vsc/fig-sb1.png";
import imgSb2 from "../../../images/vsc/fig-sb2.png";
import imgSb3 from "../../../images/vsc/fig-sb3.png";
import imgRenderAssembly from "../../../images/vsc/fig-render-assembly.png";
import imgRenderExploded from "../../../images/vsc/fig-render-exploded.png";
import imgRenderElec from "../../../images/vsc/fig-render-elec.png";
import imgUi from "../../../images/vsc/fig-ui-panel.jpg";
import imgLifestyle from "../../../images/vsc/fig-lifestyle.png";
import imgRenderArch from "../../../images/vsc/fig-render-arch.png";

const FRAME = { w: 1920, h: 1080 };
const pct = (x, y) => ({ x: (x / FRAME.w) * 100, y: (y / FRAME.h) * 100 });

// Dots sit on the outer end of each original leader line.
const BAG_POINTS = [
  { label: "UI Panel", ...pct(1473, 415), side: "right" },
  { label: "Bag Outlet", ...pct(1473, 661), side: "right" },
  { label: "Bag Dispenser", ...pct(1471, 772), side: "right" },
  { label: "Bag dispenser cover", ...pct(1473, 982), side: "right" },
];
const BAG_LINES = [
  [1313, 415, 1473, 415],
  [1184, 661, 1473, 661],
  [1184, 772, 1471, 772],
  [1142, 982, 1473, 982],
  [1141, 838, 1141, 982],
];

const SEALER_POINTS = [
  { label: "Steam Nozzle", ...pct(541, 93), side: "right" },
  { label: "Bag holder", ...pct(1257, 245), side: "right" },
  { label: "Sealing Bar", ...pct(1614, 584), side: "right" },
];
const SEALER_LINES = [
  [342, 93, 541, 93],
  [342, 92, 342, 254],
  [789, 245, 1257, 245],
  [1146, 584, 1614, 584],
];

const ASSEMBLY_POINTS = [
  { label: "Pusher", ...pct(431, 318), side: "left" },
  { label: "Perforator", ...pct(1479, 313), side: "right" },
  { label: "Spring", ...pct(1479, 354), side: "right" },
  { label: "Sealer", ...pct(1479, 397), side: "right" },
  { label: "Cutter", ...pct(360, 410), side: "left" },
  { label: "Dispenser Body", ...pct(1479, 512), side: "right" },
  { label: "Worm Gear", ...pct(1479, 581), side: "right" },
  { label: "Spring VB Holder", ...pct(367, 875), side: "left" },
  { label: "Step Motor", ...pct(757, 879), side: "right" },
];
const ASSEMBLY_LINES = [
  [431, 318, 710, 318],
  [994, 313, 1479, 313],
  [1031, 354, 1479, 354],
  [1046, 397, 1479, 397],
  [360, 410, 663, 410],
  [1119, 512, 1479, 512],
  [775, 581, 1479, 581],
  [824, 581, 1078, 581],
  [367, 705, 367, 875],
  [757, 629, 757, 740],
  [757, 735, 757, 879],
];

const EXPLODED_POINTS = [
  { label: "Steam Release", ...pct(1345, 105), side: "right" },
  { label: "Glass Panel", ...pct(1345, 195), side: "right" },
  { label: "Sealer", ...pct(388, 267), side: "left" },
  { label: "Sealer Heating Holder", ...pct(1345, 293), side: "right" },
  { label: "Top Case", ...pct(420, 384), side: "left" },
  { label: "Water Tank Lid", ...pct(1345, 479), side: "right" },
  { label: "Outer Shell", ...pct(491, 554), side: "left" },
  { label: "Water Tank", ...pct(1345, 583), side: "right" },
  { label: "Bottom Case", ...pct(1345, 714), side: "right" },
  { label: "Slider", ...pct(314, 824), side: "left" },
  { label: "Glass Panel Mount", ...pct(1345, 824), side: "right" },
  { label: "Glass Panel Mount II", ...pct(1348, 920), side: "right" },
  { label: "Glass Panel", ...pct(387, 965), side: "left" },
];
const EXPLODED_LINES = [
  [925, 105, 1345, 105],
  [925, 105, 925, 134],
  [1159, 195, 1345, 195],
  [388, 267, 740, 267],
  [1146, 293, 1345, 293],
  [420, 384, 705, 384],
  [1201, 479, 1345, 479],
  [1201, 479, 1201, 555],
  [491, 554, 729, 554],
  [1230, 583, 1345, 583],
  [1089, 714, 1345, 714],
  [314, 824, 615, 824],
  [1103, 824, 1345, 824],
  [1146, 920, 1348, 920],
  [387, 965, 637, 965],
];

const DRIVERS = [
  "Convenience",
  "Autonomy",
  "Time management",
  "Need of trendy meals",
  "Optimized cooking",
];

const ASSEMBLY = [
  { label: "Pusher", body: "ABS, injection molding" },
  { label: "Cutter", body: "" },
  { label: "Spring VB Holder", body: "316 Steel" },
  { label: "Perforator", body: "" },
  { label: "Spring", body: "" },
  { label: "Sealer", body: "ABS, injection molding" },
  { label: "Dispenser Body", body: "ABS, injection molding" },
  { label: "Worm Gear", body: "" },
  { label: "Step Motor", body: "" },
];

const MATERIALS = [
  { label: "Steam Release", body: "ABS, injection molding" },
  { label: "Glass Panel", body: "Acrylic" },
  { label: "Sealer", body: "ABS, injection molding" },
  { label: "Sealer Heating Holder", body: "Brass, CNC machining" },
  { label: "Top Case", body: "ABS" },
  { label: "Water Tank Lid", body: "PP" },
  { label: "Water Tank", body: "ABS, injection molding" },
  { label: "Outer Shell", body: "316 Steel, sheet press cut and bend" },
  { label: "Bottom Case", body: "316 Steel, sheet press cut and bend" },
  { label: "Glass Panel Mount", body: "316 Steel, sheet press cut and bend" },
  { label: "Glass Panel Mount II", body: "ABS, injection molding" },
];

const VSC = () => {
  useStageParallax();

  return (
    <CaseStudy
      slug="/vsc"
      title="VSC"
      tagline="Masters Design Group Workshop"
      year="2024"
      client={{ name: "Electrolux with Poli.Design" }}
      roles={[
        "Conceptualisation & sketching",
        "CAD modelling and technical drafting",
        "3D modeling & visualisation",
      ]}
      hero={{ src: imgBgIntro, alt: "VSC vacuum sealer drawer in a kitchen" }}
      lead={
        <p>
          The aim of the project was to combine a food preparation and
          preservation method in the existing AEG vacuum sealer drawer to aid
          users with a busy lifestyle in meal prepping balanced meals.
        </p>
      }
    >
      <Section id="brief" title="Counter Brief">
        <Specs items={DRIVERS} columns={3} />
        <Figure
          src={imgFood}
          alt="Portioned meal-prep ingredients"
          narrow
          max="760px"
          frame={false}
        />
      </Section>

      <Stage
        src={imgRenderOverview}
        alt="Overview render showing portioning, sealing and cooking"
        title="Three moments in one drawer."
        height="100dvh"
      />

      <Section id="overview">
        <Specs items={["Portioning", "Sealing", "Cooking"]} columns={3} />
      </Section>

      <Section id="bag" title="Bag Dispenser">
        <Annotated
          src={imgRenderBag}
          alt="Cutaway render of the bag dispenser assembly"
          points={BAG_POINTS}
          lines={BAG_LINES}
          frame={FRAME}
          media={{ l: -1, t: -521, w: 2017, h: 1750 }}
        />
      </Section>

      <Section id="sealer" title="Vacuum Sealer">
        <Annotated
          src={imgRenderSealer}
          alt="Detail render of the vacuum sealer, steam nozzle and sealing bar"
          points={SEALER_POINTS}
          lines={SEALER_LINES}
          frame={FRAME}
          media={{ l: 0, t: 0, w: 2174, h: 1222 }}
        />
      </Section>

      <Stage
        src={imgSteamCooking}
        alt="VSC drawer set into a kitchen wall, releasing steam from the condensation outlet"
        title="Steam Cooking"
        height="90dvh"
      >
        <p>Condensation outlet.</p>
      </Stage>

      <Section
        id="steam"
        title="Steam cooking, two ways"
        lead="With a tray, or directly in the sealed bag."
      />

      <div className="vsc-duo">
        <figure>
          <img src={imgVeg} alt="Vegetables steam cooked on the tray" loading="lazy" />
          <figcaption className="u-mono">Steam cooking with tray</figcaption>
        </figure>
        <figure>
          <img src={imgSalmon} alt="Salmon steam cooked inside a sealed bag" loading="lazy" />
          <figcaption className="u-mono">Sealed bag steam cooking</figcaption>
        </figure>
      </div>

      {/* All three boards visible at once rather than in a sideways pan. */}
      <Section id="storyboard" title="Storyboard">
        <Grid min={300}>
          <Figure src={imgSb1} alt="Storyboard frame one: portioning" />
          <Figure src={imgSb2} alt="Storyboard frame two: sealing" />
          <Figure src={imgSb3} alt="Storyboard frame three: cooking" />
        </Grid>
      </Section>

      <Section id="assembly" title="Assembly" lead="Bag dispenser.">
        <Annotated
          src={imgRenderAssembly}
          alt="Assembly render of the bag dispenser mechanism"
          points={ASSEMBLY_POINTS}
          lines={ASSEMBLY_LINES}
          frame={FRAME}
        />
        <Specs items={ASSEMBLY} columns={3} />
      </Section>

      <Section
        id="exploded"
        title="Exploded view"
        lead="Production and materials."
      >
        <Annotated
          src={imgRenderExploded}
          alt="Exploded view of the full VSC drawer"
          points={EXPLODED_POINTS}
          lines={EXPLODED_LINES}
          frame={FRAME}
        />
        <Specs items={MATERIALS} columns={3} />
      </Section>

      <Stage
        src={imgRenderArch}
        alt="Steam flow architecture diagram"
        title="Architecture"
        height="96dvh"
      >
        <p>
          Steam flow: water is held in the tank, driven through the nozzle and
          released back out through the condensation outlet.
        </p>
      </Stage>

      <Section id="electronics">
        <figure className="vsc-overlay" data-reveal>
          <img
            src={imgRenderElec}
            alt="Internal electronics layout of the VSC drawer"
            loading="lazy"
            decoding="async"
          />
          <h2 className="cs-h2" id="electronics-h">
            Electronics
          </h2>
        </figure>
      </Section>

      <Section id="ui" title="Interface">
        <Figure
          src={imgUi}
          alt="VSC control interface panel"
          narrow
          max="960px"
        />
      </Section>

      <Stage
        src={imgLifestyle}
        alt="VSC drawer in a finished kitchen"
        title="Meal prep, built into the drawer."
        height="100dvh"
      />
    </CaseStudy>
  );
};

export default VSC;
